import { useQuery } from '@tanstack/react-query';
import { KeyRoundIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Input } from '@/components/ui/input.js';
import {
  authFlightsQueryOptions,
  observeTakeoverStream,
  pendingInputsQueryOptions,
  QualityApiError,
  useAnswerAuthFlight,
  useAnswerInput,
  useResetAuthFlightEnvironment,
} from '@/lib/api/quality.js';
import type {
  AuthFlight,
  DeviceTakeoverRequest,
  PendingInputRequest,
  RequestDescriptor,
  ResetAuthFlightBody,
  ValueInputKind,
  ValueInputRequest,
} from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';

import { DeviceTakeoverRow } from './DeviceTakeoverRow.js';
import { useTickingNow } from './elapsed.js';
import { KIND_LABEL } from './help-request-kind-label.js';

const ALERTED_HELP_IDS_MAX = 500;
const ALERTED_HELP_ID_TTL_MS = 60 * 60 * 1000;
const alertedHelpIds = new Map<string, number>();

export function resetHelpNeededAlertsForTest(): void {
  alertedHelpIds.clear();
}

export function alertedHelpIdsSizeForTest(): number {
  return alertedHelpIds.size;
}

export function rememberHelpIdsForTest(ids: string[], nowMs: number): string[] {
  return rememberUnseenHelpIds(ids, nowMs);
}

const LEGACY_REQUEST_LABEL: Record<ValueInputKind, string> = {
  credential: 'Sign-in value required',
  otp: 'One-time code required',
  text: 'Additional value required',
};

/** Fixed acknowledgement sent for a `field === 'action'` request — the service only needs a
 *  non-empty answer to unblock the run, it never stores or reuses the value (`action` is not
 *  reuse-eligible — `request-identity.ts`'s `isReusableField`). The confirm control never offers a
 *  free-text field, so this is the only value that path can ever send. */
export const ACTION_ACKNOWLEDGEMENT_VALUE = 'Confirmed by operator';

/** Human-facing label for each closed `RequestedField` (AC2's taxonomy rendered as prose) —
 * mirrors the service's own `FIELD_LABELS` in `request-descriptor.ts` byte-for-byte, since AC4
 * requires the SPA and Teams to show the same wording from the same parts. Deliberately not a
 * superset of every field the service may ever add — an unrecognized `field` falls back to the
 * kind label in `describeRequest`, per the "unknown key" rule for API-cast unions. */
const REQUESTED_FIELD_LABEL: Record<string, string> = {
  username: 'Username',
  password: 'Password',
  otp: 'One-time code',
  base_url: 'Base URL',
  step_anchor: 'Value',
  other: 'Value',
  action: 'Action',
};

/** Composes the operator-facing label from the server-supplied `descriptor` parts, never from
 * worker-authored text — except `instruction`, gated to `field === 'action'` (Andrei's ruling,
 * 2026-08-12: the barrier runs in reverse for an action request, since only the model can say
 * what to do from what is on screen). Mirrors the service's `describeRequest`
 * (`request-descriptor.ts`) — see `request-descriptor.fixture.ts`'s parity spec, copied
 * byte-for-byte into this repo, for the shared expected-output table (AC4). Falls back to
 * `fallbackLabel` when there is no descriptor (a pre-T3 pending row) or the `field` is one this
 * build doesn't recognize. The `field === 'action'` gate is redundant with the normalizer's own
 * (`normalizeDescriptor`, `quality.ts`) — kept here too so a hand-built descriptor can never
 * render an instruction under the wrong field, whatever produced it. */
export function describeRequest(
  descriptor: RequestDescriptor | undefined,
  fallbackLabel: string,
): string {
  const fieldLabel = descriptor
    ? REQUESTED_FIELD_LABEL[descriptor.field]
    : undefined;
  if (!descriptor || !fieldLabel) return fallbackLabel;
  const parts = [fieldLabel];
  if (descriptor.environment_name) {
    parts.push(`on ${descriptor.environment_name}`);
  }
  if (descriptor.case_ref) parts.push(`(${descriptor.case_ref})`);
  const line = parts.join(' ');
  const instruction =
    descriptor.field === 'action' ? descriptor.instruction : null;
  const trailingText = descriptor.step_text ?? instruction;
  return trailingText ? `${line}: ${trailingText}` : line;
}

/** Picks the status copy for the "Other input" section header. The three states are mutually
 * exclusive by construction (`isAnchorOnly` and `isActionOnly` are each computed from `pending`
 * with `Array.every`, so at most one is true), so a plain `if`/`else` reads clearer than a
 * chained ternary. */
function describePendingSectionStatus(
  isAnchorOnly: boolean,
  isActionOnly: boolean,
): string {
  if (isAnchorOnly) {
    return 'Breakpoint: the test case requests your input at this step.';
  }
  if (isActionOnly) {
    return 'The run is waiting for you to confirm an action. Confirm below once it is done to let the run continue.';
  }
  return 'The run is waiting for an additional value. Submit it below to let the run continue.';
}

function stableHelpIds(
  runId: string,
  pending: ValueInputRequest[],
  takeovers: DeviceTakeoverRequest[],
  flights: AuthFlight[],
): string[] {
  return [
    ...pending.map((request) => `${runId}:input:${request.request_id}`),
    ...takeovers.map((request) => `${runId}:takeover:${request.request_id}`),
    ...flights.map((flight) => `${runId}:flight:${flight.flight_id}`),
  ].sort();
}

function unseenTakeoverIds(
  runId: string,
  takeovers: DeviceTakeoverRequest[],
  unseenIds: string[],
): string[] {
  const unseen = new Set(unseenIds);
  return takeovers
    .filter((request) => unseen.has(`${runId}:takeover:${request.request_id}`))
    .map((request) => request.request_id);
}

function rememberUnseenHelpIds(ids: string[], nowMs: number): string[] {
  pruneAlertedHelpIds(nowMs);
  const unseenIds = ids.filter((id) => !alertedHelpIds.has(id));
  for (const id of unseenIds) alertedHelpIds.set(id, nowMs);
  pruneAlertedHelpIds(nowMs);
  return unseenIds;
}

function pruneAlertedHelpIds(nowMs: number): void {
  for (const [id, seenAt] of alertedHelpIds) {
    if (nowMs - seenAt > ALERTED_HELP_ID_TTL_MS) alertedHelpIds.delete(id);
  }
  while (alertedHelpIds.size > ALERTED_HELP_IDS_MAX) {
    const oldest = alertedHelpIds.keys().next().value;
    if (typeof oldest !== 'string') return;
    alertedHelpIds.delete(oldest);
  }
}

async function playHelpNeededTone(): Promise<boolean> {
  const AudioContextCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) return false;
  let context: AudioContext | null = null;
  try {
    context = new AudioContextCtor();
    if (context.state === 'suspended') {
      await context.resume();
    }
    if (context.state !== 'running') {
      await context.close().catch(() => undefined);
      return false;
    }
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.16);
    const runningContext = context;
    window.setTimeout(() => {
      void runningContext.close().catch(() => undefined);
    }, 250);
    return true;
  } catch {
    await context?.close?.().catch(() => undefined);
    return false;
  }
}

const ACTIVE_FLIGHT_STATES = new Set<AuthFlight['state']>([
  'pending',
  'applying',
  'account_mismatch',
  'expired',
]);

interface EnvironmentFlightGroup {
  key: string;
  environmentName: string;
  resetSelector: ResetAuthFlightBody | null;
  flights: AuthFlight[];
  waitingCaseCount: number;
}

/** Shared mid-run HITL surface — every way a run can ask a human for help, in one block.
 *
 * To the human these are the same thing: supply a value, or drive the UI yourself. Auth requests use
 * the environment-scoped AuthFlight control plane; a non-auth request_user_input row stays answerable
 * through its compatibility endpoint; and a device takeover — the agent stuck on a control it cannot
 * drive at all — rides the same list, discriminated by `kind` (see HelpRequest). No path renders
 * worker-authored prompt/key text or ever echoes a submitted value. */
export function InputRequiredPanel({
  runId,
  terminal = false,
}: {
  runId: string;
  terminal?: boolean;
}): JSX.Element | null {
  const inputQuery = useQuery(pendingInputsQueryOptions(runId, !terminal));
  const flightQuery = useQuery(authFlightsQueryOptions(runId));
  /**
   * A disabled query still hands back whatever it cached, so on a run that just went terminal the
   * legacy rows would linger and claim the run is waiting for input. Flights deliberately survive
   * terminal (they explain the failure); legacy pending rows do not.
   */
  const help = terminal ? [] : (inputQuery.data ?? []);
  const takeovers = help.filter(
    (request): request is DeviceTakeoverRequest =>
      request.kind === 'device_takeover' &&
      request.has_stream &&
      Boolean(request.stream_token),
  );
  const pending = help.filter(
    (request): request is ValueInputRequest =>
      request.kind !== 'device_takeover',
  );
  const flights = (flightQuery.data ?? []).filter((flight) =>
    ACTIVE_FLIGHT_STATES.has(flight.state),
  );
  const groups = groupAuthFlights(flights);
  const otherHelpActive = pending.length > 0 || groups.length > 0;
  /**
   * Auto-expand a takeover that is the only actionable thing asking for help, so a human arriving
   * from a Teams deep link sees the device immediately. A value or authentication row keeps the live
   * view closed: the stream must not run while someone enters a secret next to it.
   */
  const soleTakeoverId =
    !otherHelpActive && takeovers.length === 1
      ? takeovers[0]!.request_id
      : null;
  const now = useTickingNow(
    flights.some(
      (flight) =>
        (flight.state === 'pending' || flight.state === 'account_mismatch') &&
        flight.expires_at != null,
    ) || pending.some(hasLiveExpiry),
  );
  const isAnchorOnly =
    pending.length > 0 &&
    pending.every(
      (request: ValueInputRequest) =>
        request.key === 'HITL_INPUT' || request.key === 'HITL_INPUT_NOMEM',
    );
  const isActionOnly =
    pending.length > 0 &&
    pending.every(
      (request: PendingInputRequest) => request.descriptor?.field === 'action',
    );
  const actionableHelpCount = pending.length + takeovers.length;
  const waitingCount =
    actionableHelpCount +
    groups.reduce((total, group) => total + group.waitingCaseCount, 0);
  const pendingSectionStatusText = describePendingSectionStatus(
    isAnchorOnly,
    isActionOnly,
  );
  const [soundBlocked, setSoundBlocked] = useState(false);
  const alertKey = stableHelpIds(runId, pending, takeovers, flights).join('|');

  useEffect(() => {
    if (!alertKey) return;
    const unseenIds = rememberUnseenHelpIds(alertKey.split('|'), Date.now());
    if (unseenIds.length === 0) return;
    const takeoverAlertIds = unseenTakeoverIds(runId, takeovers, unseenIds);
    const alertAttemptedAt = new Date().toISOString();
    void playHelpNeededTone().then((played) => {
      setSoundBlocked(!played);
      for (const requestId of takeoverAlertIds) {
        void Promise.resolve(
          observeTakeoverStream(runId, requestId, {
            event: 'alert',
            request_id: requestId,
            occurred_at: alertAttemptedAt,
            alert_played: played,
            alert_blocked: !played,
          }),
        ).catch(() => undefined);
      }
    });
  }, [alertKey, runId, takeovers]);

  /**
   * A failing legacy-inputs query must never hide an authentication card that loaded fine: the
   * run is blocked until someone answers it, and a transient 5xx on the other endpoint would
   * otherwise take away the only control that unblocks it. Surface the error only when there is
   * nothing else to show.
   */
  if (inputQuery.isError && groups.length === 0) {
    return (
      <LoadErrorCard
        isFetching={inputQuery.isFetching}
        onRetry={() => inputQuery.refetch()}
      />
    );
  }

  if (actionableHelpCount === 0 && groups.length === 0) {
    if (flightQuery.isError) {
      return (
        <LoadErrorCard
          isFetching={flightQuery.isFetching}
          onRetry={() => flightQuery.refetch()}
        />
      );
    }
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRoundIcon className="h-4 w-4" aria-hidden />
          Help needed
          <Badge variant={terminal ? 'destructive' : 'warning'}>
            {waitingCount}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {flightQuery.isError ? (
          <div className="flex flex-wrap items-center gap-2" role="status">
            <p className="text-[12px] text-destructive">
              Couldn&apos;t refresh authentication requests.
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={flightQuery.isFetching}
              onClick={() => flightQuery.refetch()}
            >
              {flightQuery.isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        ) : null}

        {soundBlocked ? (
          <p
            className="rounded-md border border-warning-500/30 bg-warning-500/5 p-2 text-[12px] text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Sound alert was blocked by the browser. Help is needed for this
            Quality run.
          </p>
        ) : null}

        {groups.length > 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-[12px] text-muted-foreground">
              Authentication is shared by cases using the same environment.
              Values are hidden and are never shown again after submission.
            </p>
            {groups.map((group) => (
              <EnvironmentAuthGroup
                key={group.key}
                runId={runId}
                group={group}
                now={now}
                terminal={terminal}
              />
            ))}
          </div>
        ) : null}

        {takeovers.length > 0 ? (
          <section className="flex flex-col gap-3" aria-label="Device takeover">
            <p className="text-[12px] text-muted-foreground">
              The run is stuck on a control it cannot drive. Drive the device
              below to let it continue.
            </p>
            <ul className="flex flex-col gap-3">
              {takeovers.map((request) => (
                <DeviceTakeoverRow
                  key={request.request_id}
                  runId={runId}
                  request={request}
                  autoExpand={request.request_id === soleTakeoverId}
                  blockedByOtherHelp={otherHelpActive}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {pending.length > 0 ? (
          <section className="flex flex-col gap-3" aria-label="Other input">
            <p className="text-[12px] text-muted-foreground">
              {pendingSectionStatusText}
            </p>
            <ul className="flex flex-col gap-3">
              {pending.map((request) => (
                <InputRequestRow
                  key={request.request_id}
                  runId={runId}
                  request={request}
                  now={now}
                />
              ))}
            </ul>
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}

function LoadErrorCard({
  isFetching,
  onRetry,
}: {
  isFetching: boolean | undefined;
  onRetry: () => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRoundIcon className="h-4 w-4" aria-hidden />
          Help needed
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p className="text-[12px] text-destructive">
          Couldn&apos;t check whether this run is waiting on input.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          disabled={isFetching}
          onClick={onRetry}
        >
          {isFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </CardContent>
    </Card>
  );
}

function groupAuthFlights(flights: AuthFlight[]): EnvironmentFlightGroup[] {
  const groups = new Map<string, EnvironmentFlightGroup>();
  for (const flight of flights) {
    const rootFlightId = flight.parent_flight_id ?? flight.flight_id;
    const key = flight.environment_id
      ? `id:${flight.environment_id}`
      : flight.environment_reset_key
        ? `reset:${flight.environment_reset_key}`
        : flight.environment_alias
          ? `alias:${flight.environment_alias.toLowerCase()}`
          : `flight:${rootFlightId}`;
    const resetSelector: ResetAuthFlightBody | null = flight.environment_id
      ? { environmentId: flight.environment_id }
      : flight.environment_reset_key
        ? {
            environmentId: null,
            environmentResetKey: flight.environment_reset_key,
          }
        : flight.environment_alias
          ? {
              environmentId: null,
              environmentAlias: flight.environment_alias,
            }
          : null;
    const existing = groups.get(key);
    if (existing) {
      existing.flights.push(flight);
      existing.waitingCaseCount += flight.waiting_case_count;
    } else {
      groups.set(key, {
        key,
        environmentName: flight.environment_name,
        resetSelector,
        flights: [flight],
        waitingCaseCount: flight.waiting_case_count,
      });
    }
  }
  return [...groups.values()].map((group) => ({
    ...group,
    flights: group.flights.sort(
      (left, right) => Number(left.is_exception) - Number(right.is_exception),
    ),
  }));
}

function EnvironmentAuthGroup({
  runId,
  group,
  now,
  terminal,
}: {
  runId: string;
  group: EnvironmentFlightGroup;
  now: number;
  terminal: boolean;
}): JSX.Element {
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useResetAuthFlightEnvironment(runId);
  const resetSelector = group.resetSelector;
  const waitingLabel = `${group.waitingCaseCount} waiting ${group.waitingCaseCount === 1 ? 'case' : 'cases'}`;

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-border bg-background p-3"
      aria-label={`Authentication for ${group.environmentName}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-medium">{group.environmentName}</h4>
          <Badge variant="outline">{waitingLabel}</Badge>
        </div>
        {resetSelector ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={reset.isPending}
            aria-label={`Reset authentication for ${group.environmentName}`}
            onClick={() => setConfirmReset(true)}
          >
            {reset.isPending ? 'Resetting…' : 'Reset'}
          </Button>
        ) : null}
      </div>

      {confirmReset && resetSelector ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning-500/30 bg-warning-500/5 p-2"
          role="alert"
        >
          <p className="text-[12px] text-muted-foreground">
            Clear saved authentication only for {group.environmentName}?
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmReset(false)}
            >
              Keep
            </Button>
            <Button
              size="sm"
              variant="destructive"
              aria-label={`Confirm reset for ${group.environmentName}`}
              disabled={reset.isPending}
              onClick={() =>
                reset.mutate(resetSelector, {
                  onSuccess: (result) => {
                    setConfirmReset(false);
                    if (result.cleanup_status === 'pending') {
                      notify.warning(
                        `Authentication reset for ${group.environmentName}, but stored session cleanup is incomplete.`,
                      );
                    } else {
                      notify.success(
                        `Authentication reset for ${group.environmentName}.`,
                      );
                    }
                  },
                  onError: () =>
                    notify.error(
                      'Failed to reset authentication. Refresh and try again.',
                    ),
                })
              }
            >
              Confirm reset
            </Button>
          </div>
        </div>
      ) : null}

      <ul className="flex flex-col gap-2">
        {group.flights.map((flight) => (
          <AuthFlightRow
            key={flight.flight_id}
            runId={runId}
            flight={flight}
            environmentName={group.environmentName}
            now={now}
            terminal={terminal}
          />
        ))}
      </ul>
    </section>
  );
}

function AuthFlightRow({
  runId,
  flight,
  environmentName,
  now,
  terminal,
}: {
  runId: string;
  flight: AuthFlight;
  environmentName: string;
  now: number;
  terminal: boolean;
}): JSX.Element {
  const [value, setValue] = useState('');
  const answer = useAnswerAuthFlight(runId);
  const isExpired = flight.state === 'expired';
  const isApplying = flight.state === 'applying';
  const isMismatch =
    flight.state === 'account_mismatch' || flight.account_mismatch;
  const flightLabel = describeRequest(flight.descriptor, flight.safe_label);

  function submit(): void {
    if (answer.isPending || isApplying || isExpired) return;
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      notify.warning('Enter a value before applying authentication.');
      return;
    }
    answer.mutate(
      { flightId: flight.flight_id, body: { value: trimmedValue } },
      {
        onSuccess: () => {
          setValue('');
          notify.success(
            'Authentication submitted — applying to waiting cases.',
          );
        },
        onError: () =>
          notify.error(
            'Failed to apply authentication. Refresh and try again.',
          ),
      },
    );
  }

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border/70 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {isMismatch ? <Badge variant="warning">Account mismatch</Badge> : null}
        {isExpired ? (
          <Badge variant="destructive">Authentication expired</Badge>
        ) : null}
        {!isMismatch && !isExpired ? (
          <Badge variant="outline">Authentication</Badge>
        ) : null}
        <span className="text-[12px]">{flightLabel}</span>
      </div>

      {isExpired ? (
        <p className="text-[12px] text-destructive" role="status">
          {terminal
            ? 'The run failed because authentication expired before it was provided.'
            : 'Authentication expired. The run is being failed safely.'}
        </p>
      ) : null}

      {isApplying ? (
        <p className="text-[12px] text-muted-foreground" role="status">
          Applying authentication…
        </p>
      ) : null}

      {!isExpired && !isApplying ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <Input
            aria-label={`Authentication value for ${environmentName}: ${flightLabel}`}
            type="password"
            autoComplete="new-password"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            placeholder="Enter the value (hidden)"
          />
          <Button size="sm" disabled={answer.isPending} onClick={submit}>
            {answer.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      ) : null}

      {flight.expires_at ? (
        <p className="text-[11px] text-muted-foreground" role="timer">
          {expiryLabel(flight.expires_at, now)}
        </p>
      ) : null}
    </li>
  );
}

/** True when a value request is still pending and carries a parseable expiry to count down to. */
function hasLiveExpiry(request: ValueInputRequest): boolean {
  return (
    request.state === 'pending' &&
    request.expires_at != null &&
    Number.isFinite(Date.parse(request.expires_at))
  );
}

function expiryLabel(expiresAt: string, now: number): string {
  const expiryMs = Date.parse(expiresAt);
  const absolute = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(expiryMs));
  const remainingMs = Math.max(0, expiryMs - now);
  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return `Expires ${absolute} · ${countdownLabel(days, hours, minutes, seconds)} remaining`;
}

function countdownLabel(
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
): string {
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Compatibility path for one non-auth request. The raw `key` stays internal and is never
 * rendered — only the server-composed `descriptor` (or, absent one, the kind label) reaches DOM. */
function InputRequestRow({
  runId,
  request,
  now,
}: {
  runId: string;
  request: ValueInputRequest;
  now: number;
}): JSX.Element {
  const [value, setValue] = useState('');
  const [treatAsSecret, setTreatAsSecret] = useState(false);
  const isHITLInput = request.key === 'HITL_INPUT';
  const [remember, setRemember] = useState(isHITLInput);
  const answer = useAnswerInput(runId);
  const isAction = request.descriptor?.field === 'action';
  const isTextKind = request.kind === 'text';
  const secret = !isTextKind || treatAsSecret;
  const requestLabel = describeRequest(
    request.descriptor,
    LEGACY_REQUEST_LABEL[request.kind],
  );

  /** The only path an action row's answer can ever take — no value the operator typed, no
   *  kind/remember override. `field === 'action'` is not reuse-eligible on the service, so there
   *  is nothing to gain from letting the operator type anything here. */
  function onConfirmAction(): void {
    if (answer.isPending) return;
    answer.mutate(
      {
        requestId: request.request_id,
        body: { value: ACTION_ACKNOWLEDGEMENT_VALUE },
      },
      {
        onSuccess: () => notify.success('Confirmed — the run will continue.'),
        onError: (error) => notify.error(errMsg(error, 'Failed to confirm')),
      },
    );
  }

  function onSubmit(): void {
    if (answer.isPending) return;
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      notify.warning('Enter a value before submitting.');
      return;
    }
    answer.mutate(
      {
        requestId: request.request_id,
        body: {
          value: trimmedValue,
          ...(isTextKind && treatAsSecret
            ? { kind: 'credential' as const }
            : {}),
          ...(isTextKind && !treatAsSecret && remember
            ? { remember: true }
            : {}),
        },
      },
      {
        onSuccess: () => {
          notify.success('Value submitted — the run will continue.');
          setValue('');
          setTreatAsSecret(false);
          setRemember(false);
        },
        onError: (error) =>
          notify.error(errMsg(error, 'Failed to submit the value')),
      },
    );
  }

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border bg-background p-3">
      <div className="flex items-center gap-2">
        <Badge variant="outline">
          {isAction ? 'Action' : KIND_LABEL[request.kind]}
        </Badge>
        <span className="text-[12px]">{requestLabel}</span>
      </div>
      {isAction ? (
        <Button
          size="sm"
          className="self-start"
          disabled={answer.isPending}
          onClick={onConfirmAction}
          aria-label={`Confirm ${requestLabel}`}
        >
          {answer.isPending ? 'Confirming…' : 'Confirm'}
        </Button>
      ) : (
        <>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Input
                aria-label={requestLabel}
                type={secret ? 'password' : 'text'}
                autoComplete={secret ? 'new-password' : 'off'}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onSubmit();
                }}
                placeholder={
                  secret ? 'Enter the value (hidden)' : 'Enter the value'
                }
              />
            </div>
            <Button size="sm" disabled={answer.isPending} onClick={onSubmit}>
              {answer.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
          {isTextKind ? (
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxRow
                checked={treatAsSecret}
                onChange={(checked) => {
                  setTreatAsSecret(checked);
                  if (checked) setRemember(false);
                }}
                label="This is a secret (store as a credential)"
              />
              {!treatAsSecret ? (
                <CheckboxRow
                  checked={remember}
                  onChange={setRemember}
                  label="Remember for next runs"
                />
              ) : null}
            </div>
          ) : null}
        </>
      )}
      {hasLiveExpiry(request) && request.expires_at ? (
        <p className="text-[11px] text-muted-foreground" role="timer">
          {expiryLabel(request.expires_at, now)}
        </p>
      ) : null}
    </li>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onChange(!checked);
        }
      }}
      className="flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Checkbox
        checked={checked}
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span>{label}</span>
    </div>
  );
}

function errMsg(error: unknown, fallback: string): string {
  if (error instanceof QualityApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}
